import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

// Pelip Brand · button styles
// font-display (Outfit) + weight 500 según Style Guide v1.0
// primary: glow + lift en hover · secondary: surface + border-strong · ghost: hover bg elev
const buttonVariants = cva(
  "font-display inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-all duration-200 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default:
          "bg-pelip-primary text-white hover:bg-pelip-primary-hover hover:-translate-y-px hover:shadow-[0_8px_24px_-8px_#2563EB]",
        destructive:
          "bg-pelip-danger text-pelip-text hover:bg-pelip-danger/90",
        outline:
          "border border-pelip-border-strong bg-transparent text-pelip-text-soft hover:border-pelip-text-muted hover:text-pelip-text",
        secondary:
          "bg-pelip-bg-elev-2 text-pelip-text border border-pelip-border-strong hover:border-pelip-text-muted",
        ghost: "text-pelip-text-soft hover:bg-pelip-bg-elev-2 hover:text-pelip-text",
        link: "text-pelip-accent underline-offset-4 hover:underline",
      },
      size: {
        default: "h-10 px-[18px] py-[10px]",
        sm: "h-8 rounded-md px-3 text-xs",
        lg: "h-12 rounded-md px-6 text-base",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }
